import React from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { useTheme } from '@veneer/theme';
import { doubleFocusRing } from '../utils/focus_ring';

const webkit = {
  corner: '::-webkit-scrollbar-corner',
  scrollbar: '::-webkit-scrollbar',
  thumb: '::-webkit-scrollbar-thumb',
  track: '::-webkit-scrollbar-track',
} as const;

const useStyles = ({
  applyTo,
  cascadeStyle,
  roundMargin,
  sharpMargin,
  pillMargin,
}: {
  applyTo?: string;
  cascadeStyle?: boolean;
  roundMargin?: string;
  sharpMargin?: string;
  pillMargin?: string;
}): {
  base: SerializedStyles;
  customScroll: SerializedStyles;
  focusRing: SerializedStyles;

  alwaysVisible: SerializedStyles;
} => {
  const theme = useTheme();

  return React.useMemo(() => {
    const size = `calc(${theme.layout.size3} + 2px)`;
    const border = theme.layout.size1;

    const scrollbarMargin =
      theme.shape === 'round'
        ? roundMargin || theme.layout.size1
        : theme.shape === 'pill'
          ? pillMargin || theme.layout.size4
          : sharpMargin || border;

    const getWebkitValue = (value: keyof typeof webkit) => {
      return `${webkit[value]}${cascadeStyle ? `, *${webkit[value]}` : ''}`;
    };

    return {
      base: css({
        overflow: 'auto',
      }),

      customScroll: css({
        // Fallback to customize browser scrollbar if we can't set our own using pseudos
        '@supports not selector(::-webkit-scrollbar-thumb)': {
          scrollbarColor: `${theme.color.stroke.informative.default} transparent`,
          // `scrollbarWidth` is not inherited by its children https://github.com/w3c/csswg-drafts/issues/4799
          scrollbarWidth: 'thin',
        },

        // Otherwise, if we _do_ support ::-webkit-scrollbar-thumb ensure that scrollbar-color is auto otherwise
        // some browsers (blink based) will ignore ::-webkit-scrollbar selectors
        scrollbarColor: 'auto',

        // Chrome, Edge, and Safari
        [getWebkitValue('corner')]: {
          width: size,
          height: size,
        },

        [getWebkitValue('scrollbar')]: {
          height: size,
          width: size,
          '&:hover': {
            backgroundColor: theme.color.background.container.default,
          },

          ':vertical:hover': {
            borderLeft: `1px solid ${theme.color.stroke.decorative.default}`,
          },

          ':horizontal:hover': {
            borderTop: `1px solid ${theme.color.stroke.decorative.default}`,
          },
        },

        [getWebkitValue('thumb')]: {
          transition: 'background-color 0.2s ease',
          backgroundClip: 'padding-box',
          backgroundColor: 'transparent',
          border: `${border} solid transparent`,
          borderRadius: theme.borderRadius3,
          minHeight: theme.layout.size6,
          minWidth: theme.layout.size6,

          ':vertical:hover, :horizontal:hover': {
            backgroundColor: theme.color.stroke.informative.hover,
          },

          ':vertical:active, :horizontal:active': {
            backgroundColor: theme.color.stroke.informative.active,
          },
        },

        ':hover, :focus-visible': {
          [getWebkitValue('thumb')]: {
            backgroundColor: theme.color.stroke.informative.default,
          },
        },

        [getWebkitValue('track')]: {
          marginBlock: scrollbarMargin,
          marginInline: scrollbarMargin,
        },

        [`.${applyTo}`]: {
          ...doubleFocusRing({ theme, shadowSize: 2 }),
        },
      }),

      alwaysVisible: css({
        [getWebkitValue('thumb')]: {
          backgroundColor: theme.color.stroke.informative.default,
        },
      }),

      focusRing: css({
        ...doubleFocusRing({ theme, shadowSize: 2 }),
      }),
    };
  }, [cascadeStyle, roundMargin, sharpMargin, pillMargin, theme, applyTo]);
};

export default useStyles;
